import { Component, ErrorInfo, ReactNode } from 'react'
import { isRouteErrorResponse, useRouteError } from 'react-router-dom'
import { Banner } from './Banner'
import { Button } from './Button'
import buttonStyles from './Button.module.scss'
import styles from './ErrorBoundary.module.scss'

interface Props {
    children: ReactNode
}

interface State {
    hasError: boolean
    error: Error | null
}

/**
 * Top-level error boundary that catches unhandled errors during rendering.
 * This prevents the app from going white when something unexpected throws.
 */
class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = { hasError: false, error: null }
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error }
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error)
        console.error('Component stack:', errorInfo.componentStack)
        // TODO: Send to error tracking service (Sentry, etc.)
    }

    render() {
        if (this.state.hasError) {
            return <ErrorFallback error={this.state.error} />
        }

        return this.props.children
    }
}

/** The fallback screen itself, shared by the class boundary and the router's errorElement. */
function ErrorFallback({ error }: { error: Error | null }) {
    return (
        <div className={styles.errorBoundary}>
            <div className={styles.content}>
                <h1>Out of Bounds</h1>
                <p>We&apos;ve logged the fault and will be back on serve shortly.</p>

                {import.meta.env.DEV && error && (
                    <div className={styles.devError}>
                        <Banner variant="error" message={error.message} />
                        <pre>{error.stack}</pre>
                    </div>
                )}

                <div className={styles.actions}>
                    <Button onClick={() => window.location.reload()}>Reload Page</Button>
                    <a href="/" className={`${buttonStyles.button} ${buttonStyles.secondary} ${buttonStyles.medium}`}>
                        Go to Home
                    </a>
                </div>
            </div>
        </div>
    )
}

/**
 * The same screen, as a data-router `errorElement`. A router catches anything
 * thrown while rendering its own routes before the class boundary above can
 * see it, so the route tree needs its own copy or the user gets React Router's
 * raw "Unexpected Application Error!" stack instead.
 */
export function RouteErrorBoundary() {
    const error = useRouteError()
    console.error('Route error:', error)
    const asError = error instanceof Error
        ? error
        : isRouteErrorResponse(error)
            ? new Error(`${error.status} ${error.statusText}`)
            : new Error(String(error))
    return <ErrorFallback error={asError} />
}

export default ErrorBoundary
